import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { SpotlightCard, SpotlightCardContent } from "./SpotlightCard";
import { GradientText, SectionLabel } from "./GradientText";
import { Quote } from "lucide-react";

interface Testimonial {
  id: string;
  quote?: string;
  author?: string;
  role?: string;
  avatar: string;
}

interface TestimonialsProps {
  testimonials: Testimonial[];
}

/**
 * Testimonials
 *
 * Grid of testimonial cards with:
 * - Spotlight hover effect
 * - Avatar images with fallback
 * - Quote icon decoration
 * - Text from translations keyed by testimonial id
 */
export function Testimonials({ testimonials }: TestimonialsProps) {
  const { t } = useTranslation("landing");
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  const title = t("testimonials.title");
  const subtitle = t("testimonials.subtitle");

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="testimonials" className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          className={cn(
            "mx-auto mb-16 max-w-3xl text-center lg:mb-20",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
          )}
        >
          <SectionLabel className="mb-6">Testimonials</SectionLabel>

          <h2 className="mb-4 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            <GradientText>{title}</GradientText>
          </h2>

          {subtitle && <p className="text-base text-muted-foreground lg:text-lg">{subtitle}</p>}
        </div>

        {/* Testimonials grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {testimonials.map((testimonial, index) => {
            const quote = t(`testimonials.${testimonial.id}.quote`, {
              defaultValue: testimonial.quote,
            });
            const author = t(`testimonials.${testimonial.id}.author`, {
              defaultValue: testimonial.author,
            });
            const role = t(`testimonials.${testimonial.id}.role`, {
              defaultValue: testimonial.role,
            });

            return (
              <SpotlightCard
                key={testimonial.id}
                className={cn(
                  "transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                  isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
                )}
                style={{
                  transitionDelay: isVisible ? `${index * 80}ms` : "0ms",
                }}
              >
                <SpotlightCardContent className="flex h-full flex-col p-6">
                  {/* Quote icon */}
                  <Quote className="mb-4 h-8 w-8 text-primary/40" />

                  {/* Quote text */}
                  <blockquote className="mb-6 flex-1 text-base leading-relaxed text-foreground">
                    &ldquo;{quote}&rdquo;
                  </blockquote>

                  {/* Author */}
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div
                      className={cn(
                        "h-10 w-10 overflow-hidden rounded-full",
                        "bg-muted",
                        "flex items-center justify-center",
                      )}
                    >
                      {testimonial.avatar ? (
                        <img
                          src={testimonial.avatar}
                          alt={author}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            // Fallback to initials on error
                            const target = e.target as HTMLImageElement;
                            target.style.display = "none";
                            target.parentElement!.innerHTML = `<span class="text-primary font-semibold text-sm">${(
                              author || ""
                            )
                              .split(" ")
                              .map((n: string) => n[0])
                              .join("")}</span>`;
                          }}
                        />
                      ) : (
                        <span className="text-sm font-semibold text-primary">
                          {(author || "")
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </span>
                      )}
                    </div>

                    {/* Name & Role */}
                    <div>
                      <div className="text-sm font-medium text-foreground">{author}</div>
                      <div className="text-xs text-muted-foreground">{role}</div>
                    </div>
                  </div>
                </SpotlightCardContent>
              </SpotlightCard>
            );
          })}
        </div>
      </div>
    </section>
  );
}
